/**
 * Contexte d'authentification : login → MFA (obligatoire selon les droits —
 * Q50) → session ; profil courant ; thème (Q99) ; déconnexion.
 *
 * Changer de compte change aussi de base hors ligne et vide le cache des
 * requêtes : rien d'un compte ne s'affiche dans la session d'un autre
 * (WEB1-01, WEB1-18).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ActivityTrade, EquipmentTrackingMode, SpecializedModule, TradeProfile, TenantBranding, WorkingHours,
} from '@myequip/shared';
import { api, ApiError, hasSession, holdRequests, onMemberChange, setTabMember, setTokens } from './api.js';
import { t } from './i18n.js';
import {
  ME_CACHE, MEMBER_KEY, membershipsOf, readPendingInvitation, storedMember, writeStoredMember, type Membership,
} from './memberships.js';
import { passkeyLogin, setupMfaWithPasskey } from './passkeys.js';
import { rememberAccount } from './remembered-account.js';
import { releasePushOnLogout, syncPush } from './push.js';
import { DEFAULT_THEME, resolvedTheme, THEMES, type Theme } from './theme.js';
import { releaseAccountData, settleAccountRuns, summarizeUnsentWork, switchAccount } from '../offline/sync.js';
import type { StoreScope } from '../offline/store.js';

export interface Me {
  id: string;
  /** Session d'assistance MyEquip : `writeEnabled` faux pour une assistance en lecture. */
  support_session?: { id: string; actorId: string; writeEnabled: boolean };
  username: string | null;
  email: string | null;
  avatar_url: string | null;
  avatar_file_id: string | null;
  google_oauth_enabled: boolean;
  /** Un compte Google est lié à ce compte (il peut être délié depuis Mon compte). */
  google_linked: boolean;
  full_name: string;
  role: 'patron' | 'dispatcher' | 'technicien';
  mfa_enabled: boolean;
  mfa_required?: boolean;
  /** Code par e-mail choisi en secours, encore permis (compte sans droit sensible). */
  mfa_email?: boolean;
  company_name: string;
  /** Fuseau de l'entreprise (IANA) : heures murales des créneaux répétés, comme le serveur.
   *  Absent d'un profil gardé avant le 23/10/2026 : Paris. */
  company_timezone?: string;
  /** Patron-technicien : non-technicien exerçant aussi sur le terrain. Autorise
   *  la bascule « Mode terrain » sans changer de compte ni de droits serveur. */
  field_operator: boolean;
  onboarding_state: Record<string, unknown>;
  /** Raccourci choisi pour la barre basse, tenu par le serveur pour être le même
   *  sur tous les appareils (le premier compte : les anciens comptes en ont
   *  enregistré deux) ; null : défaut de la vue. */
  nav_shortcuts?: string[] | null;
  /** null tant que l'adresse n'a pas été confirmée par son titulaire. */
  email_verified_at: string | null;
  /** Capacités EFFECTIVES, celles du rôle porté. Guide d'affichage seulement :
   *  l'API refuse de toute façon ce qui n'est pas autorisé. */
  capabilities: string[];
  /** Vues du bureau (tableaux de bord, présence), décidées par les droits du
   *  rôle comme côté API ; jamais pendant une session d'assistance. */
  office_access: boolean;
  /** Place dans la hiérarchie de l'entreprise ; l'API reste seule juge. */
  hierarchy?: { rank: number; manageScope: 'below' | 'all'; roleId: string | null; roleName: string | null } | null;
  /** Identité visuelle de l'entreprise (Q102) — remplace les défauts plateforme. */
  branding: TenantBranding | null;
  primary_trade: TradeProfile;
  additional_trades?: ActivityTrade[];
  enabled_modules: SpecializedModule[];
  /** Heures habituelles de l'entreprise (absentes d'un profil gardé avant le 24/10/2026). */
  working_hours?: WorkingHours;
  /** Réglage « machines suivies » de l'entreprise et sa valeur effective. */
  equipment_tracking_mode?: EquipmentTrackingMode;
  equipment_tracking?: boolean;
  /** Mise en route au niveau entreprise (partagée par tous ses utilisateurs). */
  company_onboarding_state: Record<string, unknown>;
  /** État d'abonnement, pour annoncer une échéance AVANT le premier refus. */
  subscription: SubscriptionState | null;
  /** Usages de la position choisis par l'entreprise (Q59, 16/10/2026). */
  location_features?: { eta: boolean; nearest: boolean; steps: boolean };
  /** Entreprises du compte personnel (sélecteur) ; vide en assistance et dans un profil gardé avant le 25/10/2026. */
  memberships?: Membership[];
}

export interface SubscriptionState {
  /** `sans_abonnement` : aucune ligne d'abonnement, l'accès est fermé (refus du serveur). */
  status: 'essai' | 'actif' | 'grace' | 'lecture_seule' | 'suspendu' | 'termine' | 'sans_abonnement';
  trialEndsAt: string | null;
  /** Jours restants avant l'échéance d'essai ; négatif si elle est passée. */
  daysLeft: number | null;
  /** Suspendu ou terminé, fonction allumée : sans reprise, ses clients verront d'autres artisans. */
  orphanCustomers?: boolean;
  /** Terminé : dernier jour de la récupération des données (30 jours après la fin, décision 12). */
  recoveryUntil?: string | null;
}

export type { TenantBranding } from '@myequip/shared';

export type LoginStep =
  | { status: 'ok' }
  /** Code attendu : celui de l'appli (`totp`) ou celui reçu par e-mail (`email`). `emailCode` :
   *  le compte, sans droit sensible, peut aussi finir par un code reçu par e-mail. */
  | { status: 'mfa_required'; mfaToken: string; trustedDeviceDays?: number; method?: 'totp' | 'email'; emailCode?: boolean }
  | { status: 'mfa_setup_required'; setupToken: string; emailCode?: boolean }
  /** Double authentification assurée par une clé d'accès : la connexion se termine par elle. */
  | { status: 'passkey_required'; mfaToken?: string; emailCode?: boolean }
  /** Compte prouvé, plusieurs entreprises possibles : la personne choisit (jeton de compte à usage unique, 10 min). */
  | { status: 'choose_company'; choices: { userId: string; companyName: string }[]; accountToken: string }
  /** Compte prouvé sans entreprise en service : ses invitations, ou enregistrer son entreprise. */
  | {
    status: 'no_company'; invitations: { companyName: string; expiresAt: string }[];
    /** Entreprises où une place de comptable attend la fiche du compte. */
    advisorPending?: string[]; accountToken: string;
  }
  /** Invitation en attente d'acceptation sur cet appareil : la connexion y ramène (`InvitationPage`). */
  | { status: 'invitation'; token: string; accountToken?: string };

/** Réponse d'une fin de connexion : jetons de session, ou étape suivante. */
type LoginResponse =
  | { status?: 'ok'; accessToken?: string; refreshToken?: string; trustedDeviceToken?: string }
  | (Exclude<LoginStep, { status: 'ok' | 'invitation' }> & { trustedDeviceToken?: string });

/** Code envoyé par e-mail : défi à renvoyer avec le code, adresse masquée pour la reconnaître. */
export interface EmailCodeSent { challengeToken: string; emailHint: string; expiresInSeconds: number }

/**
 * Jeton d'appareil de confiance : remis après un code validé avec « ne plus
 * demander sur cet appareil », renvoyé avec le mot de passe aux connexions
 * suivantes. Il ne connecte personne à lui seul et survit à la déconnexion —
 * c'est son rôle ; « Oublier cet appareil » dans les paramètres le retire.
 */
const TRUSTED_DEVICE_KEY = 'fa.trusted';

export function forgetTrustedDevice(): void {
  localStorage.removeItem(TRUSTED_DEVICE_KEY);
}

export type { Theme } from './theme.js';

/** Vue active d'un patron-technicien : pilotage (bureau) ou terrain (technicien). */
export type FieldMode = 'pilotage' | 'terrain';

interface AuthContextValue {
  me: Me | undefined;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<LoginStep>;
  /** Connexion par clé d'accès ; `autofill` : clé choisie dans les suggestions du champ identifiant. */
  loginWithPasskey: (autofill?: boolean) => Promise<LoginStep>;
  verifyMfa: (mfaToken: string, code: string, rememberDevice?: boolean) => Promise<LoginStep>;
  setupMfa: (setupToken: string) => Promise<{ otpauthUri: string; secret: string }>;
  /** Codes de secours à montrer, puis l'étape suivante (`finishLogin` une fois les codes gardés). */
  activateMfa: (setupToken: string, code: string) => Promise<{ backupCodes: string[]; next: LoginResponse }>;
  activateMfaWithPasskey: (setupToken: string) => Promise<LoginStep>;
  /** Envoie un code par e-mail pour l'étape désignée (jeton de code attendu ou de mise en place). */
  sendEmailCode: (token: string) => Promise<EmailCodeSent>;
  verifyEmailCode: (token: string, challengeToken: string, code: string, rememberDevice?: boolean) => Promise<LoginStep>;
  /** Termine une connexion à partir de la réponse du serveur : session ouverte, ou étape suivante. */
  finishLogin: (response: LoginResponse) => Promise<LoginStep>;
  /** Connexion à plusieurs entreprises : entre dans celle choisie. */
  chooseCompany: (accountToken: string, userId: string) => Promise<LoginStep>;
  /**
   * Passe à une autre entreprise du compte, d'un geste : les envois en cours
   * finissent, le cache et le profil gardé sont vidés, la base hors ligne
   * devient celle de l'entreprise choisie (le travail non envoyé de la
   * précédente y reste, et partira à son retour). Refusé hors ligne.
   */
  switchCompany: (userId: string) => Promise<void>;
  /**
   * Ferme la session de cet appareil. Les envois pas encore partis restent dans
   * la base du compte, sauf `erase` (confirmé par l'écran de déconnexion). Une
   * assistance MyEquip avec des envois en attente ouvre cet écran et reste ouverte.
   */
  logout: (options?: { erase?: boolean }) => Promise<void>;
  reload: () => Promise<void>;
  /** Reporte dans le profil (et son cache hors ligne) une valeur que le serveur
   *  vient de CONFIRMER, sans relire tout `/auth/me`. */
  applyConfirmed: (patch: Pick<Me, 'nav_shortcuts'>) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Mode d'affichage du patron-technicien. 'pilotage' pour tout autre profil. */
  fieldMode: FieldMode;
  setFieldMode: (mode: FieldMode) => void;
}

/** Registre unique des routes externes : voir `app/external-routes.tsx`. */
import { isExternalPath } from '../app/external-routes.js';

export { isExternalPath };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readCachedMe(): Me | undefined {
  try {
    const value = JSON.parse(localStorage.getItem(ME_CACHE) ?? 'null') as Me | null;
    return value && typeof value === 'object' && typeof value.id === 'string' ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Base hors ligne d'un profil : la sienne, ou celle, jetable, d'une assistance MyEquip. */
export function storeScopeFor(user: Pick<Me, 'id' | 'support_session'> | undefined): StoreScope | undefined {
  if (!user) return undefined;
  return user.support_session
    ? { kind: 'support', userId: user.id, sessionId: user.support_session.id }
    : { kind: 'account', userId: user.id };
}

/** Aucune réponse d'un compte ne survit à sa session (WEB1-18) : requêtes arrêtées puis oubliées. */
export function forgetAccountQueries(client: QueryClient): void {
  void client.cancelQueries();
  client.clear();
}

/** Écran de déconnexion monté (`SignOutGuard`) : il annonce les envois en attente. */
let signOutGuard: (() => void) | undefined;

/** Branche l'écran de déconnexion ; la fonction rendue le débranche. */
export function setSignOutGuard(guard: () => void): () => void {
  signOutGuard = guard;
  return () => {
    if (signOutGuard === guard) signOutGuard = undefined;
  };
}

/**
 * Une assistance MyEquip efface sa base jetable en partant. Tant qu'un envoi y
 * attend, elle ne part qu'après confirmation explicite (`erase`) : sinon
 * l'écran de déconnexion s'ouvre, d'où qu'elle ait été demandée (bandeau,
 * vérification d'identité…), et `false` dit que la session reste ouverte.
 * Une base illisible ne retient rien, comme pour l'écran de déconnexion.
 */
export async function supportExitConfirmed(erase: boolean): Promise<boolean> {
  if (erase) return true;
  const unsent = await summarizeUnsentWork().catch(() => []);
  if (unsent.length === 0) return true;
  signOutGuard?.();
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const [me, setMeState] = useState<Me | undefined>();
  const [loading, setLoading] = useState(true);
  const accountId = useRef<string | undefined>(undefined);
  // Profil appliqué : la base hors ligne est choisie AVANT le rendu qui la lit,
  // et un autre compte (ou plus aucun) ne retrouve rien du cache du précédent.
  const setMe = useCallback((next: Me | undefined) => {
    if (accountId.current !== undefined && accountId.current !== next?.id) {
      forgetAccountQueries(queryClient);
    }
    accountId.current = next?.id;
    switchAccount(storeScopeFor(next));
    // Les envois de l'onglet partent pour cette adhésion seulement (`api.ts`).
    setTabMember(next && !next.support_session ? next.id : undefined);
    setMeState(next);
  }, [queryClient]);
  const meRef = useRef<Me | undefined>(undefined);
  meRef.current = me;
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('fa.theme') as Theme | null;
    return stored && THEMES.includes(stored) ? stored : DEFAULT_THEME;
  });
  const [fieldMode, setFieldModeState] = useState<FieldMode>(
    (localStorage.getItem('fa.fieldmode') as FieldMode | null) ?? 'pilotage',
  );

  useEffect(() => {
    const apply = (): void => {
      const resolved = resolvedTheme(theme, new Date());
      document.documentElement.dataset.theme = resolved;
      // Relu par index.html au prochain chargement, avant le premier affichage.
      if (theme === 'heure') localStorage.setItem('fa.theme.heure', resolved);
    };
    apply();
    if (theme !== 'heure') return undefined;
    // Le lever et le coucher du soleil arrivent pendant que l'application est ouverte.
    const timer = window.setInterval(apply, 60_000);
    document.addEventListener('visibilitychange', apply);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', apply);
    };
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    localStorage.setItem('fa.theme', next);
  }, []);

  const setFieldMode = useCallback((next: FieldMode) => {
    setFieldModeState(next);
    localStorage.setItem('fa.fieldmode', next);
  }, []);

  const reload = useCallback(async () => {
    // Espaces externes (portail client, sous-traitant, super-admin, pages
    // publiques) : ils gèrent LEUR propre session. Ne pas déclencher ici le
    // bootstrap interne `/auth/me` — sur ces routes il échouait (401) et le
    // refresh interne effaçait la session du portail au rechargement (défaut relevé).
    if (isExternalPath(window.location.pathname)) {
      setMe(undefined);
      setLoading(false);
      return;
    }
    if (!hasSession()) {
      localStorage.removeItem(ME_CACHE);
      setMe(undefined);
      setLoading(false);
      return;
    }
    try {
      const fresh = await api<Me>('/auth/me');
      localStorage.setItem(ME_CACHE, JSON.stringify(fresh));
      // Seul un profil relu au serveur dit à quelle adhésion sont les jetons ;
      // un profil gardé (hors ligne) ne la réécrit jamais.
      if (!fresh.support_session) writeStoredMember(fresh.id);
      setMe(fresh);
    } catch (error) {
      // Réseau coupé ou serveur en redémarrage pendant un rechargement : la
      // session existe toujours. On garde le dernier profil connu pour rester sur
      // la page (hors ligne compris) au lieu d'afficher l'écran de connexion.
      // Les droits restent vérifiés par le serveur à chaque appel.
      // Réseau absent, ou proxy qui répond 502/503 pendant un redémarrage :
      // jamais une session refusée (401/403), qui renvoie bien à la connexion.
      const cached = error instanceof ApiError
        && (error.code === 'network_error' || error.code === 'internal_error')
        ? readCachedMe()
        : undefined;
      if (cached) {
        setMe(cached);
        return;
      }
      localStorage.removeItem(ME_CACHE);
      if(localStorage.getItem('fa.kind')==='support'&&sessionStorage.getItem('myequip.admin.access')) {
        window.location.replace('/admin');
      }
      setMe(undefined);
    } finally {
      setLoading(false);
    }
  }, [setMe]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Notifications push (décision 1) : l'abonnement de ce navigateur suit la
  // personne connectée, jamais celle d'avant ni une session d'assistance.
  // Les entreprises d'un même compte gardent l'abonnement : c'est la même personne.
  const pushUser = me && !me.support_session ? me.id : undefined;
  useEffect(() => {
    if (!pushUser) return;
    const sameAccount = membershipsOf(meRef.current).map((m) => m.userId);
    void syncPush(pushUser, sameAccount).catch(() => undefined);
  }, [pushUser]);

  // Compte retenu par l'appareil pour la connexion suivante (`remembered-account.ts`) :
  // son identifiant et son nom, et si une clé d'accès vient de servir ici.
  const usedPasskey = useRef(false);
  const rememberedId = me && !me.support_session ? me.id : undefined;
  useEffect(() => {
    if (!me || !rememberedId) return;
    const identifier = me.username ?? me.email;
    if (identifier) rememberAccount({ identifier, name: me.full_name, ...(usedPasskey.current ? { passkey: true } : {}) });
    usedPasskey.current = false;
  }, [rememberedId]); // une fois par compte ouvert

  const applyConfirmed = useCallback((patch: Pick<Me, 'nav_shortcuts'>) => {
    // Même compte : seule la valeur confirmée change, ni base ni cache.
    setMeState((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      try {
        localStorage.setItem(ME_CACHE, JSON.stringify(next));
      } catch {
        /* le profil en mémoire suffit jusqu'au prochain chargement */
      }
      return next;
    });
  }, []);

  /**
   * Fin de connexion commune (mot de passe, code, clé d'accès, mise en place,
   * choix d'entreprise) : la session s'ouvre, ou l'étape suivante est rendue.
   * Une invitation en attente sur cet onglet (`InvitationPage`) reprend la main :
   * la personne y confirme l'entreprise rejointe.
   */
  const finishLogin = useCallback(async (response: LoginResponse): Promise<LoginStep> => {
    if (response.trustedDeviceToken) localStorage.setItem(TRUSTED_DEVICE_KEY, response.trustedDeviceToken);
    const invitation = readPendingInvitation();
    if (response.status === 'choose_company' || response.status === 'no_company') {
      return invitation ? { status: 'invitation', token: invitation.token, accountToken: response.accountToken } : response;
    }
    if (response.status && response.status !== 'ok') return response;
    localStorage.removeItem(ME_CACHE);
    forgetAccountQueries(queryClient);
    setTokens(response.accessToken, response.refreshToken);
    if (invitation) return { status: 'invitation', token: invitation.token };
    await reload();
    return { status: 'ok' };
  }, [reload, queryClient]);

  const login = useCallback(async (identifier: string, password: string): Promise<LoginStep> => {
    const platform = 'web';
    const result = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: {
        identifier, password, platform,
        trustedDeviceToken: localStorage.getItem(TRUSTED_DEVICE_KEY) ?? undefined,
      },
    });
    return finishLogin(result);
  }, [finishLogin]);

  const loginWithPasskey = useCallback(async (autofill = false) => {
    const result = await passkeyLogin(autofill);
    usedPasskey.current = true;
    return finishLogin(result);
  }, [finishLogin]);

  const verifyMfa = useCallback(async (mfaToken: string, code: string, rememberDevice = false) => finishLogin(
    await api<LoginResponse>('/auth/mfa/verify', { method: 'POST', body: { mfaToken, code, rememberDevice } }),
  ), [finishLogin]);

  const setupMfa = useCallback(
    (setupToken: string) =>
      api<{ otpauthUri: string; secret: string }>('/auth/mfa/setup', {
        method: 'POST',
        body: { setupToken },
      }),
    [],
  );

  const activateMfaWithPasskey = useCallback(async (setupToken: string) => {
    const result = await setupMfaWithPasskey(setupToken);
    usedPasskey.current = true;
    return finishLogin(result);
  }, [finishLogin]);

  const sendEmailCode = useCallback(
    (token: string) => api<EmailCodeSent>('/auth/mfa/email/send', { method: 'POST', body: { token } }),
    [],
  );

  const verifyEmailCode = useCallback(async (token: string, challengeToken: string, code: string, rememberDevice = false) =>
    finishLogin(await api<LoginResponse>(
      '/auth/mfa/email/verify', { method: 'POST', body: { token, challengeToken, code: code.trim(), rememberDevice } })),
  [finishLogin]);

  const activateMfa = useCallback(async (setupToken: string, code: string) => {
    const { backupCodes, ...next } = await api<LoginResponse & { backupCodes: string[] }>(
      '/auth/mfa/activate', { method: 'POST', body: { setupToken, code } });
    localStorage.removeItem(ME_CACHE);
    forgetAccountQueries(queryClient);
    // PAS de session ouverte ici : recharger `me` ferait basculer le routeur et
    // démonterait l'écran AVANT l'affichage des codes de secours (défaut
    // détecté par l'audit). LoginPage appelle `finishLogin(next)` après
    // confirmation explicite que les codes sont sauvegardés.
    return { backupCodes, next: next as LoginResponse };
  }, [queryClient]);

  const chooseCompany = useCallback(async (accountToken: string, userId: string) => finishLogin(
    await api<LoginResponse>('/auth/login/choose', { method: 'POST', body: { accountToken, userId } }),
  ), [finishLogin]);

  /**
   * Profil de l'adhésion des jetons, relu au serveur, appliqué à l'onglet : base
   * hors ligne, cache des requêtes vidé, adhésion de l'onglet. Hors réseau, rien
   * ne change : l'onglet reste sur l'entreprise affichée, sans rien envoyer.
   */
  const enterMember = useCallback(async () => {
    localStorage.removeItem(ME_CACHE);
    const fresh = await api<Me>('/auth/me');
    localStorage.setItem(ME_CACHE, JSON.stringify(fresh));
    writeStoredMember(fresh.id);
    setMe(fresh);
  }, [setMe]);

  const switchCompany = useCallback(async (userId: string) => {
    const current = meRef.current;
    if (!current || current.support_session || userId === current.id) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new Error(t('compteEntreprises.horsLigne'));
    holdRequests(true);
    try {
      // Un envoi ou un pull en cours finit dans la base de l'entreprise quittée.
      await settleAccountRuns();
      const switched = await api<{ accessToken: string; userId: string }>(
        '/auth/switch', { method: 'POST', body: { userId } });
      // L'adhésion d'abord : un autre onglet qui lit les nouveaux jetons sait déjà qu'il doit suivre.
      writeStoredMember(switched.userId);
      setTokens(switched.accessToken);
      await enterMember();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'network_error') throw new Error(t('compteEntreprises.horsLigne'), { cause: error });
      throw error;
    } finally {
      holdRequests(false);
    }
  }, [enterMember]);

  // Les jetons ont changé d'entreprise ailleurs (autre onglet, renouvellement
  // reporté sur une autre entreprise du compte) : l'onglet suit.
  const following = useRef(false);
  useEffect(() => {
    const follow = (): void => {
      const current = meRef.current;
      const target = storedMember();
      if (!current || current.support_session || !target || target === current.id || following.current) return;
      following.current = true;
      holdRequests(true);
      void settleAccountRuns()
        .then(enterMember)
        .catch(() => undefined) // réseau : nouvel essai au retour du réseau ou au premier plan
        .finally(() => {
          following.current = false;
          holdRequests(false);
        });
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.key === MEMBER_KEY || event.key === null) follow();
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') follow();
    };
    const stop = onMemberChange(follow);
    window.addEventListener('storage', onStorage);
    window.addEventListener('online', follow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop();
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('online', follow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enterMember]);

  const logout = useCallback(async (options: { erase?: boolean } = {}) => {
    const scope = storeScopeFor(me);
    if (me?.support_session) {
      // Envois en attente : jamais effacés sans la confirmation de l'écran de déconnexion.
      if (!(await supportExitConfirmed(Boolean(options.erase)))) return;
      // La base jetable de l'assistance s'efface avant de rendre la main à la console.
      forgetAccountQueries(queryClient);
      switchAccount(undefined);
      if (scope) await releaseAccountData(scope, 'erase').catch(() => undefined);
      setTokens(undefined, '');
      window.location.href = `/admin?endSupport=${me.support_session.id}`;
      return;
    }
    // Plus aucune alerte de ce compte sur cet appareil une fois déconnecté.
    await releasePushOnLogout();
    try {
      // Seule la session de cet appareil est fermée ; les autres restent ouvertes.
      await api('/auth/logout', {
        method: 'POST',
        body: { refreshToken: localStorage.getItem('fa.refresh') ?? undefined },
      });
    } catch {
      // hors ligne : la session locale est quand même effacée
    }
    setTokens(undefined, '');
    writeStoredMember(undefined);
    forgetAccountQueries(queryClient);
    setMe(undefined);
    // Copies du serveur retirées ; ce qui n'est pas parti reste dans la base du
    // compte, illisible par un autre. Un échec ne rouvre pas la session : la
    // base reste cloisonnée et sera rangée à la prochaine déconnexion.
    if (scope) await releaseAccountData(scope, options.erase ? 'erase' : 'keep').catch(() => undefined);
  }, [me, queryClient, setMe]);

  const value = useMemo(
    () => ({ me, loading, login, loginWithPasskey, verifyMfa, setupMfa, activateMfa, activateMfaWithPasskey,
      sendEmailCode, verifyEmailCode, finishLogin, chooseCompany, switchCompany,
      logout, reload, applyConfirmed, theme, setTheme, fieldMode, setFieldMode }),
    [me, loading, login, loginWithPasskey, verifyMfa, setupMfa, activateMfa, activateMfaWithPasskey,
      sendEmailCode, verifyEmailCode, finishLogin, chooseCompany, switchCompany,
      logout, reload, applyConfirmed, theme, setTheme, fieldMode, setFieldMode],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Contexte figé de l'aperçu de démonstration (fond de la page de connexion) :
 * un profil fictif, aucune session, aucun stockage. Toute tentative de
 * connexion ou de changement échoue sans rien toucher.
 */
export function PreviewAuthProvider({ me, children }: { me: Me; children: ReactNode }): ReactNode {
  const value = useMemo<AuthContextValue>(() => {
    const refuse = (): Promise<never> => Promise.reject(new Error('Aperçu : aucune action possible.'));
    return {
      me, loading: false, login: refuse, loginWithPasskey: refuse, verifyMfa: refuse, setupMfa: refuse, activateMfa: refuse, activateMfaWithPasskey: refuse,
      sendEmailCode: refuse, verifyEmailCode: refuse, finishLogin: refuse, chooseCompany: refuse, switchCompany: refuse,
      logout: refuse, reload: async () => undefined, applyConfirmed: () => undefined,
      theme: 'auto', setTheme: () => undefined, fieldMode: 'pilotage', setFieldMode: () => undefined,
    };
  }, [me]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth hors AuthProvider');
  return ctx;
}

/**
 * Logique PURE du contexte d'interface (testable sans rendu). `true` = expérience
 * TERRAIN : un technicien (toujours) ou un patron-technicien ayant choisi
 * « Terrain ». C'est un choix de PRÉSENTATION, jamais d'autorisation — l'API
 * valide toujours par le rôle et les capacités ; ce booléen (dérivé d'une
 * préférence frontend modifiable) ne doit gouverner aucun droit serveur.
 */
export function terrainViewActive(
  user: Pick<Me, 'role' | 'field_operator'> | undefined,
  fieldMode: FieldMode,
): boolean {
  if (!user) return false;
  return user.role === 'technicien' || (user.field_operator && fieldMode === 'terrain');
}

/** Contexte d'interface centralisé (évite les comparaisons de rôle dispersées). */
export function useTerrainView(): boolean {
  const { me, fieldMode } = useAuth();
  return terrainViewActive(me, fieldMode);
}

/**
 * Membre de terrain (rôle de base technicien, rôles sur mesure compris) : seule
 * la PRÉSENTATION en dépend — la bascule Pilotage/Terrain n'a pas de sens pour
 * lui. Les droits, eux, se lisent avec `useCan`.
 */
export function isFieldWorker(user: Pick<Me, 'role'> | undefined): boolean {
  return user?.role === 'technicien';
}

/** Abonnement qui ne donne plus accès à rien (le serveur refuse toutes les données). */
export function isClosedSubscription(status: SubscriptionState['status'] | undefined): boolean {
  return status === 'suspendu' || status === 'termine' || status === 'sans_abonnement';
}

/**
 * Responsable de l'entreprise (rôle de base patron) : l'API lui réserve les
 * gestes qui engagent l'entreprise elle-même (clore une assistance MyEquip,
 * demander la reprise de l'abonnement). L'interface ne fait que suivre.
 */
export function isCompanyOwner(user: Pick<Me, 'role'> | undefined): boolean {
  return user?.role === 'patron';
}

/**
 * Capacité effective de l'utilisateur courant. Sert à NE PAS afficher une
 * entrée de menu ou une action qui produirait un 403 : l'autorisation réelle
 * reste vérifiée par l'API sur chaque route.
 */
export function useCan(): (capability: string) => boolean {
  const { me } = useAuth();
  return useCallback(
    (capability: string) => me?.capabilities?.includes(capability) ?? false,
    [me],
  );
}

/** Affichage conditionné au profil métier ; l'API conserve le dernier mot. */
export function useModule(module: SpecializedModule): boolean {
  const { me } = useAuth();
  return me?.enabled_modules?.includes(module) ?? false;
}
